import PropTypes from 'prop-types';
import { CircularProgress } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AttachFileIcon from '@/component/common/icons/AttachFileIcon';
import OverflowTooltipContent from '@/component/common/OverflowTooltipContent';
import Utils from '@/common/utils/utils';
import './AiPreviewUploadFiles.css';

/**
 * Preview Image Item Component
 */
function AiPreviewImageItem({ image, index, onRemove, onPreview }) {
    const isUploading = !image.s3Path;

    return (
        <div className={`ai-preview-upload-item ai-preview-upload-img-item ${isUploading ? 'uploading' : ''}`}>
            <img
                src={image.localUrl}
                alt={`upload-${index}`}
                className="ai-preview-upload-img"
                onClick={() => {
                    if (!image.s3Path) return;
                    onPreview?.(image);
                }}
            />
            {isUploading && (
                <div className="ai-preview-upload-loading-wrapper">
                    <CircularProgress size={16} thickness={5} className="ai-preview-upload-loading" />
                </div>
            )}
            {!isUploading && (
                <button
                    className="ai-preview-upload-remove-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove?.(image);
                    }}
                >
                    <CloseIcon style={{ fontSize: 14 }} />
                </button>
            )}
        </div>
    );
}

AiPreviewImageItem.propTypes = {
    image: PropTypes.shape({
        id: PropTypes.string,
        localUrl: PropTypes.string,
        s3Path: PropTypes.string
    }).isRequired,
    index: PropTypes.number,
    onRemove: PropTypes.func,
    onPreview: PropTypes.func
};

/**
 * Preview File Item Component - styled like reference image
 */
function AiPreviewFileItem({ file, onRemove }) {
    const isUploading = !file.s3Path;

    return (
        <div className={`ai-preview-upload-item ai-preview-upload-file-item ${isUploading ? 'uploading' : ''}`}>
            <span className="ai-preview-file-icon">
                <AttachFileIcon extension={file?.fileExtension} background="#FFFFFF" />
            </span>
            <div className="ai-preview-file-info">
                <OverflowTooltipContent
                    text={file?.fileName}
                    className="ai-preview-file-name"
                    style={{ maxWidth: '200px' }}
                />
            </div>
            {isUploading && (
                <div className="ai-preview-upload-loading-wrapper">
                    <CircularProgress size={16} thickness={5} className="ai-preview-upload-loading" />
                </div>
            )}
            {!isUploading && (
                <button
                    className="ai-preview-upload-remove-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove?.(file);
                    }}
                >
                    <CloseIcon style={{ fontSize: 14 }} />
                </button>
            )}
        </div>
    );
}

AiPreviewFileItem.propTypes = {
    file: PropTypes.shape({
        id: PropTypes.string,
        fileName: PropTypes.string,
        fileExtension: PropTypes.string,
        fileSize: PropTypes.number,
        s3Path: PropTypes.string
    }).isRequired,
    onRemove: PropTypes.func
};

/**
 * AiPreviewUploadFiles - Combined preview for images and files
 */
function AiPreviewUploadFiles({
    uploadImgs = [],
    uploadFiles = [],
    canUploadImg = true,
    onRemoveImage,
    onRemoveFile,
    onPreviewImage
}) {
    const hasImages = canUploadImg && !Utils.isEmptyArray(uploadImgs);
    const hasFiles = !Utils.isEmptyArray(uploadFiles);

    if (!hasImages && !hasFiles) {
        return null;
    }

    return (
        <div className="ai-chat-input-preview-upload-files">
            {/* Preview Images */}
            {hasImages && uploadImgs.map((previewImg, index) => (
                <AiPreviewImageItem
                    key={previewImg.id}
                    image={previewImg}
                    index={index}
                    onRemove={onRemoveImage}
                    onPreview={onPreviewImage}
                />
            ))}

            {/* Preview Files */}
            {hasFiles && uploadFiles.map((previewFile) => (
                <AiPreviewFileItem
                    key={previewFile.id}
                    file={previewFile}
                    onRemove={onRemoveFile}
                />
            ))}
        </div>
    );
}

AiPreviewUploadFiles.propTypes = {
    uploadImgs: PropTypes.array,
    uploadFiles: PropTypes.array,
    canUploadImg: PropTypes.bool,
    onRemoveImage: PropTypes.func,
    onRemoveFile: PropTypes.func,
    onPreviewImage: PropTypes.func
};

export default AiPreviewUploadFiles;
